// viewer.follow.js — top-right map navigation rail, Hawk-style.
//
// A group of 29px buttons copied from Hawk Live's control rail
// (Hawk/web/index.html #follow-ctrl + MapLibre's compass):
//   · compass — needle shows the live bearing; click resets north; drag
//     rotates the map. Display-only while following (follow owns the
//     bearing, matching Hawk's `.following .maplibregl-ctrl-compass` rule).
//   · ⌖ — follow the heli, heading-up (map rotates so heading points up).
//   · N — follow the heli, north-up.
// The follow pair are direct-select toggles: exactly one pressed, pressing
// the active one turns follow off. While following, the map re-centres on
// the heli every sync tick (viewer.js calls `handle.update(pos)` from its
// rAF loop). A user drag on the map cancels follow; zooming does not.
//
// Since 2026-09-10 (EnergyCo: "the helicopter flies off the page"):
//   · follow starts north-up, and the mode chosen with the buttons is kept
//     in localStorage per browser. A drag cancel is deliberately not kept:
//     it is a "let me look at this" gesture, not a preference.
//   · with follow off, the map still catches up when the heli flies out
//     of the viewport — but only when it *flew* out (the previous tick was
//     on-screen). A reviewer who dragged or zoomed away on purpose has both
//     positions off-screen and is left alone.

export const FOLLOW_OFF = "off";
export const FOLLOW_NORTH = "north-up";
export const FOLLOW_HEADING = "heading-up";
export const FOLLOW_STORAGE_KEY = "vmc.followMode";

const MODES = new Set([FOLLOW_OFF, FOLLOW_NORTH, FOLLOW_HEADING]);
// A heli this close to the viewport edge counts as gone, so the catch-up
// lands before the icon actually disappears.
const EDGE_INSET_PX = 32;

function readStoredMode() {
  try {
    const v = localStorage.getItem(FOLLOW_STORAGE_KEY);
    return MODES.has(v) ? v : null;
  } catch {
    return null;
  }
}

function storeMode(mode) {
  try {
    localStorage.setItem(FOLLOW_STORAGE_KEY, mode);
  } catch {
    /* private mode / quota: the choice just does not survive a reload */
  }
}

// MapLibre-style needle: dark north half, grey south half, drawn point-up
// and rotated by the map bearing.
const COMPASS_SVG = `<svg class="vmc-compass-needle" viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
  <polygon points="12,2.5 16.5,12 7.5,12" fill="#c22"/>
  <polygon points="12,21.5 16.5,12 7.5,12" fill="#9a9a9a"/>
</svg>`;

/**
 * Mount the navigation rail into the map's top-right control stack.
 *
 * @param {Element} cornerEl container the rail is appended to
 * @param {{ map: object, getHeliState: () => ({lat:number, lon:number,
 *   heading:number|null} | null) }} deps Leaflet map (or stub) + a snapshot
 *   of the heli's current position, used to snap immediately on activation.
 * @returns {{ mode: () => string, update: (pos) => void,
 *   railEl: Element | null, dispose: () => void }}
 */
export function mountFollowControl(cornerEl, deps = {}) {
  const { map, getHeliState } = deps;
  if (!map || !cornerEl) {
    return { mode: () => FOLLOW_OFF, update: () => {}, railEl: null, dispose: () => {} };
  }

  // Rotation needs leaflet-rotate; without it there is no bearing to show
  // or drive, so the compass and the heading-up toggle are not mounted.
  const canRotate = typeof map.setBearing === "function";
  let mode = readStoredMode() ?? FOLLOW_NORTH;
  if (mode === FOLLOW_HEADING && !canRotate) mode = FOLLOW_NORTH;
  // Container point of the heli on the previous tick, for the flew-out test.
  let lastPoint = null;

  const doc = cornerEl.ownerDocument;
  const rail = doc.createElement("div");
  rail.className = "vmc-map-rail";
  rail.dataset.tour = "viewer.rail";

  // Hawk grouping: the compass floats alone; only the two follow styles
  // are attached to each other (the footprint toggle later adds its own
  // group to the same rail).
  function makeGroup() {
    const g = doc.createElement("div");
    g.className = "vmc-rail-group";
    rail.appendChild(g);
    return g;
  }

  function makeBtn(group, role, title, html, tourId) {
    const b = doc.createElement("button");
    b.type = "button";
    b.dataset.role = role;
    b.className = "vmc-rail-btn";
    b.title = title;
    b.setAttribute("aria-label", title);
    if (tourId) b.dataset.tour = tourId;
    b.innerHTML = html;
    group.appendChild(b);
    return b;
  }

  const compassBtn = canRotate
    ? makeBtn(makeGroup(), "compass",
              "Reset north (drag to rotate the map)", COMPASS_SVG, "viewer.compass")
    : null;
  const followGroup = makeGroup();
  const headingBtn = canRotate
    ? makeBtn(followGroup, "heading", "Follow the helicopter — heading up", "⌖",
              "viewer.follow")
    : null;
  const northBtn = makeBtn(followGroup, "north",
                           "Follow the helicopter — north up", "N");

  function paint() {
    headingBtn?.setAttribute(
      "aria-pressed", mode === FOLLOW_HEADING ? "true" : "false");
    northBtn.setAttribute(
      "aria-pressed", mode === FOLLOW_NORTH ? "true" : "false");
    // Follow owns the bearing: the compass still shows it but must not
    // fight the per-tick rotation (Hawk W3-F1).
    if (compassBtn) compassBtn.disabled = mode !== FOLLOW_OFF;
  }

  function paintNeedle() {
    if (!compassBtn) return;
    const needle = compassBtn.querySelector(".vmc-compass-needle");
    if (needle) needle.style.transform = `rotate(${map.getBearing?.() ?? 0}deg)`;
  }
  paint();
  paintNeedle();

  function resetBearing() {
    if (canRotate) map.setBearing(0);
  }

  // Is a container point inside the viewport, less the edge inset?
  function onScreen(pt) {
    const size = map.getSize();
    return pt.x >= EDGE_INSET_PX && pt.y >= EDGE_INSET_PX
      && pt.x <= size.x - EDGE_INSET_PX && pt.y <= size.y - EDGE_INSET_PX;
  }

  // Follow off: pan only when the heli has just left the viewport.
  function catchUp(pos) {
    if (typeof map.getSize !== "function"
        || typeof map.latLngToContainerPoint !== "function") return;
    const pt = map.latLngToContainerPoint([pos.lat, pos.lon]);
    const flewOut = lastPoint !== null && onScreen(lastPoint) && !onScreen(pt);
    lastPoint = pt;
    if (!flewOut || map._animatingZoom) return;
    map.panTo([pos.lat, pos.lon], { animate: false });
    // The heli is now at the centre; remember it there, not where it was.
    lastPoint = map.latLngToContainerPoint([pos.lat, pos.lon]);
  }

  function apply(pos) {
    if (!pos) return;
    if (mode === FOLLOW_OFF) { catchUp(pos); return; }
    // Never pan/rotate mid zoom animation: a _resetView in that window
    // desyncs Leaflet's pixel origin. The next tick recentres instead.
    if (map._animatingZoom) return;
    map.panTo([pos.lat, pos.lon], { animate: false });
    // Screen angle of a geographic heading is heading + bearing (see the
    // heli-icon compensation in viewer.js), so up-the-screen = -heading.
    // A tick without a heading keeps the last bearing rather than snapping
    // back to north.
    if (mode === FOLLOW_HEADING && pos.heading != null) map.setBearing(-pos.heading);
  }

  function setMode(next) {
    const leavingRotation = mode === FOLLOW_HEADING && next !== FOLLOW_HEADING;
    mode = next;
    lastPoint = null; // a fresh start for the flew-out test
    if (leavingRotation) resetBearing();
    paint();
    apply(getHeliState?.());
  }

  // Direct-select: pressing the active toggle turns follow off (Hawk FD11).
  // Only a button press is a preference worth keeping.
  const toggleTo = (target) => () => {
    const next = mode === target ? FOLLOW_OFF : target;
    setMode(next);
    storeMode(next);
  };
  const onHeadingClick = toggleTo(FOLLOW_HEADING);
  const onNorthClick = toggleTo(FOLLOW_NORTH);
  headingBtn?.addEventListener("click", onHeadingClick);
  northBtn.addEventListener("click", onNorthClick);

  // --- Compass: click = reset north; drag = rotate about the button. ---
  const onCompassClick = () => {
    if (mode !== FOLLOW_OFF) return;
    resetBearing();
  };
  let dragPointer = null;
  let dragStartAngle = 0;
  let dragStartBearing = 0;
  let dragMoved = false;
  const pointerAngle = (e) => {
    const r = compassBtn.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    return (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
  };
  const onCompassDown = (e) => {
    if (mode !== FOLLOW_OFF) return;
    dragPointer = e.pointerId;
    dragStartAngle = pointerAngle(e);
    dragStartBearing = map.getBearing?.() ?? 0;
    dragMoved = false;
    compassBtn.setPointerCapture?.(e.pointerId);
  };
  const onCompassMove = (e) => {
    if (dragPointer === null || e.pointerId !== dragPointer) return;
    dragMoved = true;
    map.setBearing(dragStartBearing + (pointerAngle(e) - dragStartAngle));
  };
  const onCompassUp = (e) => {
    if (dragPointer === null || e.pointerId !== dragPointer) return;
    dragPointer = null;
    // A real drag must not also fire the click-reset; swallow the click
    // that follows the pointerup.
    if (dragMoved) {
      compassBtn.addEventListener(
        "click", (ce) => ce.stopImmediatePropagation(), { capture: true, once: true });
    }
  };
  if (compassBtn) {
    compassBtn.addEventListener("click", onCompassClick);
    compassBtn.addEventListener("pointerdown", onCompassDown);
    compassBtn.addEventListener("pointermove", onCompassMove);
    compassBtn.addEventListener("pointerup", onCompassUp);
    compassBtn.addEventListener("pointercancel", onCompassUp);
  }

  cornerEl.appendChild(rail);

  // Leaflet fires `dragstart` only for user drags (the Drag handler), never
  // for programmatic panTo/setView — exactly the cancel signal we want.
  const onDragStart = () => {
    if (mode !== FOLLOW_OFF) setMode(FOLLOW_OFF);
  };
  map.on("dragstart", onDragStart);
  // Every bearing change (follow ticks, compass drag, reset) repaints the
  // needle from the single source of truth.
  map.on("rotate", paintNeedle);

  return {
    mode: () => mode,
    update: apply,
    // The footprint toggle appends itself to the same rail so the corner
    // stays one aligned group (viewer.js passes this to mountFootprintToggle).
    railEl: rail,
    dispose: () => {
      map.off("dragstart", onDragStart);
      map.off("rotate", paintNeedle);
      rail.remove();
      if (mode === FOLLOW_HEADING) resetBearing();
      mode = FOLLOW_OFF;
    },
  };
}
